import { apiRequest } from '@/utils/api-request';
import { downloadBlob } from '@/utils/download-blob';
import { sanitizeFilename } from '@/utils/filename-utils';
import { useOrgStore } from '@/stores/org-store';
import type { Skill } from './types';

const FILENAME_RE = /filename="?([^";]+)"?/i;

/** platform-api's 422: Claude's skill upload would refuse this skill. */
export class SkillNotPortableError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'SkillNotPortableError';
  }
}

/**
 * Saves the skill as platform-api packs it for Claude's skill upload:
 * `<name>.zip` holding a `<name>/` folder. When Claude would refuse the skill,
 * platform-api answers 422 with the reason, which is thrown as a
 * `SkillNotPortableError` instead; that is an expected outcome, so it isn't
 * reported to Sentry.
 *
 * With `raw`, saves the skill's files exactly as stored
 * (`<name>-files.zip`, SKILL.md at the archive's root), whatever Claude would
 * make of them.
 */
export const downloadSkill = async (
  skill: Skill,
  { raw = false }: { raw?: boolean } = {},
): Promise<void> => {
  const { currentOrg } = useOrgStore.getState();
  const orgId = currentOrg?.orgid;
  if (!orgId) throw new Error('No organization selected');

  const response = await apiRequest<Blob>({
    url: `/skills/${orgId}/${skill.skill_id}/download${raw ? '?raw=true' : ''}`,
    method: 'GET',
    target: 'platform',
    responseType: 'blob',
    suppressSentryStatuses: [422],
  });
  if (!response.data || response.detail) {
    const reason = response.detail || 'Could not download this skill';
    if (response.statusCode === 422 && !raw) {
      throw new SkillNotPortableError(reason);
    }
    throw new Error(reason);
  }

  const fromHeader = response.headers
    ?.get('content-disposition')
    ?.match(FILENAME_RE)?.[1];
  const stem = sanitizeFilename(skill.name) || 'skill';
  downloadBlob(
    response.data,
    fromHeader || (raw ? `${stem}-files.zip` : `${stem}.zip`),
  );
};
